import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useUrl } from "@/context/SiteContext";
import {
  Save, Plus, Trash2, Edit, ArrowLeft, Upload, ArrowUp, ArrowDown, X, GripVertical,
} from "lucide-react";

function mediaUrl(src) {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  if (src.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${src}`;
  return src;
}

function slugify(t) {
  return (t || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

const empty = {
  slug: "",
  number: "",
  name: "",
  tagline: "",
  summary: "",
  long_text: "",
  services: [],
  domains: [],
  programs: [],
  highlights: [],
  references: [],
  values: [],
  cover_image: "",
  gallery: [],
  meta_title: "",
  meta_description: "",
  order: 0,
};

const input = "w-full bg-white border border-black rounded-none px-4 py-3 focus:outline-none";
const smallInput = "w-full bg-white border border-black rounded-none px-3 py-2 text-sm focus:outline-none";

/**
 * String-array editor: services, domains.
 */
function StringArrayEditor({ label, items, onChange, testId }) {
  const list = items || [];
  const setAt = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
  const add = () => onChange([...list, ""]);
  const del = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="font-mono-label text-black/70">{label} ({list.length})</label>
        <button onClick={add} data-testid={`${testId}-add`} className="font-mono-label border border-black px-3 py-1 hover:bg-black hover:text-white inline-flex items-center gap-1">
          <Plus size={12} /> Adaugă
        </button>
      </div>
      {list.map((it, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <input
            className={smallInput}
            value={it}
            onChange={(e) => setAt(i, e.target.value)}
            data-testid={`${testId}-${i}`}
          />
          <button onClick={() => del(i)} className="border border-black px-3 py-2 hover:bg-red-600 hover:text-white hover:border-red-600">
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * Object-array editor for programs / highlights / references / values.
 */
function ObjectArrayEditor({ label, items, onChange, fields, testId }) {
  const list = items || [];
  const setAt = (i, key, v) => onChange(list.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)));
  const add = () => onChange([...list, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const del = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="font-mono-label text-black/70">{label} ({list.length})</label>
        <button onClick={add} data-testid={`${testId}-add`} className="font-mono-label border border-black px-3 py-1 hover:bg-black hover:text-white inline-flex items-center gap-1">
          <Plus size={12} /> Adaugă
        </button>
      </div>
      {list.map((it, i) => (
        <div key={i} className="border border-black p-4 mb-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-mono-label text-black/60 text-[10px]">#{String(i + 1).padStart(2, "0")}</span>
            <button onClick={() => del(i)} className="border border-black px-2 py-1 hover:bg-red-600 hover:text-white hover:border-red-600 text-[10px]">
              <Trash2 size={12} />
            </button>
          </div>
          {fields.map((f) => (
            <div key={f.key}>
              <label className="font-mono-label text-black/50 text-[10px] block mb-1">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea
                  rows={2}
                  className={smallInput}
                  value={it[f.key] || ""}
                  onChange={(e) => setAt(i, f.key, e.target.value)}
                />
              ) : (
                <input
                  className={smallInput}
                  value={it[f.key] || ""}
                  onChange={(e) => setAt(i, f.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Gallery editor — upload images, reorder up/down, delete.
 */
function GalleryEditor({ gallery, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const f of files) {
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/admin/media", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(data.url);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
      }
    }
    if (newUrls.length) {
      onChange([...(gallery || []), ...newUrls]);
      toast.success(`${newUrls.length} imagine(i) adăugate în galerie`);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const move = (i, dir) => {
    const list = [...gallery];
    const swap = i + dir;
    if (swap < 0 || swap >= list.length) return;
    [list[i], list[swap]] = [list[swap], list[i]];
    onChange(list);
  };

  const del = (i) => onChange(gallery.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="font-mono-label text-black/70">Galerie ({gallery?.length || 0} imagini)</label>
        <div>
          <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} data-testid="gallery-file-input" />
          <button
            onClick={() => fileRef.current.click()}
            disabled={uploading}
            data-testid="gallery-upload-btn"
            className="font-mono-label border border-black px-4 py-2 hover:bg-black hover:text-white inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Upload size={12} /> {uploading ? "Se încarcă..." : "Încarcă imagini"}
          </button>
        </div>
      </div>

      {(!gallery || gallery.length === 0) ? (
        <div className="border border-dashed border-black/40 p-10 text-center font-mono-label text-black/50">
          Nicio imagine în galerie. Încarcă pentru a începe.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {gallery.map((src, i) => (
            <div key={`${src}-${i}`} className="border border-black flex flex-col" data-testid={`gallery-item-${i}`}>
              <div className="aspect-[4/3] overflow-hidden border-b border-black bg-black/5">
                <img src={mediaUrl(src)} alt="" className="w-full h-full object-cover grayscale-img" />
              </div>
              <div className="flex items-center justify-between p-2">
                <span className="font-mono-label text-black/50 text-[10px]">#{String(i + 1).padStart(2, "0")}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    data-testid={`gallery-up-${i}`}
                    className="border border-black p-1 hover:bg-black hover:text-white disabled:opacity-30"
                    title="Sus"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === gallery.length - 1}
                    data-testid={`gallery-down-${i}`}
                    className="border border-black p-1 hover:bg-black hover:text-white disabled:opacity-30"
                    title="Jos"
                  >
                    <ArrowDown size={12} />
                  </button>
                  <button
                    onClick={() => del(i)}
                    data-testid={`gallery-del-${i}`}
                    className="border border-black p-1 hover:bg-red-600 hover:text-white hover:border-red-600"
                    title="Șterge"
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              MAIN COMPONENT                                */
/* -------------------------------------------------------------------------- */

export default function DepartmentsEditor({ onSaved }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(null); // holds slug being moved
  const url = useUrl();

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get("/admin/departments");
      setItems(data);
    } catch {
      setItems([]);
      toast.error("Eroare la încărcare");
    }
  };

  useEffect(() => { load(); }, []);

  const move = async (index, dir) => {
    if (!items) return;
    const swap = index + dir;
    if (swap < 0 || swap >= items.length) return;
    const a = items[index];
    const b = items[swap];
    setReordering(a.slug);
    // optimistic UI update
    const optimistic = [...items];
    optimistic[index] = b;
    optimistic[swap] = a;
    setItems(optimistic);
    try {
      // Swap order values in DB (use their list positions to force full reordering)
      await Promise.all([
        api.put(`/admin/departments/${a.id}`, { order: swap }),
        api.put(`/admin/departments/${b.id}`, { order: index }),
      ]);
      onSaved?.();
      // reload from server to get true canonical order
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await load();
    } finally {
      setReordering(null);
    }
  };

  const save = async () => {
    if (!editing.name || !editing.slug || !editing.number) {
      return toast.error("Nume, slug și număr sunt obligatorii");
    }
    setSaving(true);
    try {
      if (editing.id) {
        // eslint-disable-next-line no-unused-vars
        const { id, ...payload } = editing;
        await api.put(`/admin/departments/${editing.id}`, payload);
        toast.success("Departament actualizat");
      } else {
        await api.post("/admin/departments", editing);
        toast.success("Departament creat");
      }
      setEditing(null);
      onSaved?.();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Ștergi acest departament?")) return;
    await api.delete(`/admin/departments/${id}`);
    toast.success("Șters");
    onSaved?.();
    load();
  };

  if (editing) {
    return (
      <div data-testid="department-editor">
        <div className="flex items-center justify-between mb-6 sticky top-20 z-20 bg-white border border-black p-4">
          <div>
            <div className="font-mono-label text-black/60">{editing.id ? "Editare departament" : "Departament nou"}</div>
            <div className="font-display font-medium uppercase text-2xl mt-1">
              {editing.name || "Fără nume"}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setEditing(null)}
              data-testid="cancel-dept-btn"
              className="font-mono-label border border-black px-4 py-2 hover:bg-black hover:text-white inline-flex items-center gap-2"
            >
              <ArrowLeft size={14} /> Anulează
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-dept-btn"
              className="font-mono-label bg-black text-white px-6 py-2 border border-black hover:bg-white hover:text-black inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Se salvează..." : "Salvează"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT: Text fields */}
          <div className="lg:col-span-7 space-y-5">
            <div className="border border-black p-5 bg-black text-white">
              <div className="font-mono-label text-white/60 mb-3">Identitate</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-mono-label text-white/60 block mb-1 text-[10px]">Număr *</label>
                  <input
                    className={`${input} bg-white text-black`}
                    value={editing.number}
                    onChange={(e) => setEditing({ ...editing, number: e.target.value })}
                    data-testid="dept-number-input"
                    placeholder="01"
                  />
                </div>
                <div className="col-span-2">
                  <label className="font-mono-label text-white/60 block mb-1 text-[10px]">Nume *</label>
                  <input
                    className={`${input} bg-white text-black`}
                    value={editing.name}
                    onChange={(e) =>
                      setEditing({ ...editing, name: e.target.value, slug: editing.slug || slugify(e.target.value) })
                    }
                    data-testid="dept-name-input"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="font-mono-label text-white/60 block mb-1 text-[10px]">Slug (URL) *</label>
                <input
                  className={`${input} bg-white text-black`}
                  value={editing.slug}
                  onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                  data-testid="dept-slug-input"
                />
                <div className="mt-1 font-mono-label text-white/50 text-[10px]">
                  {url.departments}/{editing.slug || "..."}
                </div>
              </div>
              <div className="mt-3">
                <label className="font-mono-label text-white/60 block mb-1 text-[10px]">Ordine (sortare)</label>
                <input
                  type="number"
                  className={`${input} bg-white text-black`}
                  value={editing.order ?? 0}
                  onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || "0", 10) })}
                />
              </div>
            </div>

            <div className="border border-black p-5">
              <div className="font-mono-label text-black/60 mb-3">Text conținut</div>
              <label className="font-mono-label text-black/60 block mb-1 text-[10px]">Tagline</label>
              <input
                className={input}
                value={editing.tagline}
                onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                data-testid="dept-tagline-input"
              />
              <label className="font-mono-label text-black/60 block mb-1 mt-4 text-[10px]">Rezumat (summary)</label>
              <textarea
                rows={3}
                className={input}
                value={editing.summary}
                onChange={(e) => setEditing({ ...editing, summary: e.target.value })}
                data-testid="dept-summary-input"
              />
              <label className="font-mono-label text-black/60 block mb-1 mt-4 text-[10px]">Descriere lungă</label>
              <textarea
                rows={5}
                className={input}
                value={editing.long_text}
                onChange={(e) => setEditing({ ...editing, long_text: e.target.value })}
                data-testid="dept-longtext-input"
              />
            </div>

            <div className="border border-black p-5">
              <div className="font-mono-label text-black/60 mb-3">SEO</div>
              <label className="font-mono-label text-black/60 block mb-1 text-[10px]">
                Meta Title ({(editing.meta_title || "").length} / 60)
              </label>
              <input
                className={input}
                value={editing.meta_title || ""}
                onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })}
                data-testid="dept-meta-title"
                placeholder={`${editing.name} — SUNTEC Group`}
              />
              <label className="font-mono-label text-black/60 block mb-1 mt-4 text-[10px]">
                Meta Description ({(editing.meta_description || "").length} / 160)
              </label>
              <textarea
                rows={3}
                className={input}
                value={editing.meta_description || ""}
                onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })}
                data-testid="dept-meta-desc"
              />
            </div>

            <div className="border border-black p-5 space-y-5">
              <div className="font-mono-label text-black/60">Liste conținut</div>
              <StringArrayEditor
                label="Servicii"
                items={editing.services}
                onChange={(v) => setEditing({ ...editing, services: v })}
                testId="dept-service"
              />
              <StringArrayEditor
                label="Domenii"
                items={editing.domains}
                onChange={(v) => setEditing({ ...editing, domains: v })}
                testId="dept-domain"
              />
              <ObjectArrayEditor
                label="Programe UE"
                items={editing.programs}
                onChange={(v) => setEditing({ ...editing, programs: v })}
                testId="dept-program"
                fields={[
                  { key: "name", label: "Nume program", type: "text" },
                  { key: "amount", label: "Sumă / valoare", type: "text" },
                  { key: "desc", label: "Descriere", type: "textarea" },
                  { key: "link", label: "Link extern (opțional)", type: "text" },
                ]}
              />
              <ObjectArrayEditor
                label="Highlights (K/V)"
                items={editing.highlights}
                onChange={(v) => setEditing({ ...editing, highlights: v })}
                testId="dept-highlight"
                fields={[
                  { key: "k", label: "Valoare (ex: 4 MW)", type: "text" },
                  { key: "v", label: "Descriere", type: "text" },
                ]}
              />
              <ObjectArrayEditor
                label="Referințe"
                items={editing.references}
                onChange={(v) => setEditing({ ...editing, references: v })}
                testId="dept-reference"
                fields={[
                  { key: "title", label: "Titlu", type: "text" },
                  { key: "desc", label: "Descriere", type: "textarea" },
                ]}
              />
              <ObjectArrayEditor
                label="Valori"
                items={editing.values}
                onChange={(v) => setEditing({ ...editing, values: v })}
                testId="dept-value"
                fields={[
                  { key: "t", label: "Titlu", type: "text" },
                  { key: "d", label: "Descriere", type: "text" },
                ]}
              />
            </div>
          </div>

          {/* RIGHT: Media */}
          <div className="lg:col-span-5 space-y-5">
            <div className="border border-black p-5">
              <div className="font-mono-label text-black/60 mb-3">Cover</div>
              <label className="font-mono-label text-black/60 block mb-1 text-[10px]">URL imagine cover</label>
              <input
                className={input}
                value={editing.cover_image || ""}
                onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
                data-testid="dept-cover-input"
                placeholder="https://... sau /api/media/{id}"
              />
              {editing.cover_image && (
                <div className="mt-3 border border-black">
                  <img src={mediaUrl(editing.cover_image)} alt="cover" className="w-full aspect-video object-cover grayscale-img" />
                </div>
              )}
            </div>

            <div className="border border-black p-5">
              <GalleryEditor
                gallery={editing.gallery}
                onChange={(g) => setEditing({ ...editing, gallery: g })}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (items === null) {
    return <div className="font-mono-label" data-testid="dept-loading">Se încarcă...</div>;
  }

  return (
    <div data-testid="departments-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-label text-black/60">Departamente</div>
          <div className="font-display font-medium uppercase text-2xl tracking-tight mt-1">
            {items.length} departamente
          </div>
        </div>
        <button
          onClick={() => setEditing({ ...empty, order: items.length })}
          data-testid="new-dept-btn"
          className="font-mono-label bg-black text-white px-4 py-2 hover:bg-white hover:text-black border border-black inline-flex items-center gap-2"
        >
          <Plus size={14} /> Departament nou
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-black p-12 text-center">
          <div className="font-mono-label text-black/60">Nu există departamente.</div>
        </div>
      ) : (
        <div className="border border-black divide-y divide-black">
          {items.map((d, i) => (
            <div
              key={d.id}
              data-testid={`admin-dept-${d.slug}`}
              className="p-5 flex items-center justify-between gap-4 hover:bg-black/5 transition-colors"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0 || reordering !== null}
                    data-testid={`dept-move-up-${d.slug}`}
                    className="border border-black p-1 hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Mută sus"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1 || reordering !== null}
                    data-testid={`dept-move-down-${d.slug}`}
                    className="border border-black p-1 hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Mută jos"
                  >
                    <ArrowDown size={12} />
                  </button>
                </div>
                {d.cover_image && (
                  <div className="w-16 h-16 border border-black overflow-hidden shrink-0">
                    <img src={mediaUrl(d.cover_image)} alt="" className="w-full h-full object-cover grayscale-img" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-display font-bold uppercase truncate">
                    <span className="text-black/40 mr-2">{d.number}</span>
                    {d.name}
                  </div>
                  <div className="text-sm text-black/60 font-mono-label mt-1 truncate">
                    /{d.slug} · Galerie: {d.gallery?.length || 0} · {d.services?.length || 0} servicii
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a
                  href={url.dept(d.slug)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono-label border border-black px-3 py-2 hover:bg-black hover:text-white"
                >
                  →
                </a>
                <button
                  onClick={() => setEditing({ ...empty, ...d })}
                  data-testid={`edit-dept-${d.slug}`}
                  className="font-mono-label border border-black px-3 py-2 hover:bg-black hover:text-white"
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={() => del(d.id)}
                  data-testid={`delete-dept-${d.slug}`}
                  className="font-mono-label border border-black px-3 py-2 hover:bg-red-600 hover:text-white hover:border-red-600"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
